import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { useLocation } from 'react-router-dom';

import { DaySelector } from './components/DaySelector';
import { FullscreenButton } from './components/FullscreenButton';
import { DaysScrollContainer } from './components/DaysScrollContainer';
import { FullscreenDialog } from './components/FullscreenDialog';
import { ActivityFilter } from './components/ActivityFilter';
import { UserFlagBanner } from './components/UserFlagBanner';
import { ActivitySidePanel } from './components/ActivitySidePanel';
import { UserFlagPromptDialog } from './components/UserFlagPromptDialog';
import { useActivityData } from './utils/useActivityData';
import {
    buildActivityOptions,
    pruneSelectedActivities,
    ActivityFilterItem
} from './utils/activityFilter.utils';
import { UserContext } from '..';
import type { ActivityItem } from './components/ActivityList';
import type { UserFlagSummaryDto } from '../../../core/api/FitnessActivityClient';

const FlagsTool = observer(() => {
    const user = React.useContext(UserContext);
    const location = useLocation();

    const { date } = (location.state as { date?: string | Date }) || {};

    const initialDate = (() => {
        if (!date) return new Date();
        if (typeof date === 'string') {
            const [y, m, d] = date.split('T')[0].split('-').map(Number);
            return new Date(y, m - 1, d);
        }
        return date;
    })();
    const [selectedDates, setSelectedDates] = useState<Date[]>([
        new Date(
            initialDate.getFullYear(),
            initialDate.getMonth(),
            initialDate.getDate()
        )
    ]);

    const [selectedActivities, setSelectedActivities] = useState<
        ActivityFilterItem[]
    >([]);

    const [isFullscreen, setIsFullscreen] = useState(false);

    const [selectedActivity, setSelectedActivity] =
        useState<ActivityItem | null>(null);
    const [sidePanelOpen, setSidePanelOpen] = useState(false);
    const [promptDialogOpen, setPromptDialogOpen] = useState(false);
    const [newUserFlagId, setNewUserFlagId] = useState<number | null>(null);

    const { dayDataArray, rawDayDataArray, isLoading, hasError, refetch } =
        useActivityData(user.id.toString(), selectedDates, selectedActivities);

    const availableOptions = useMemo(
        () => buildActivityOptions(rawDayDataArray),
        [rawDayDataArray]
    );

    useEffect(() => {
        if (isLoading || hasError) return;

        setSelectedActivities((current) =>
            pruneSelectedActivities(current, availableOptions)
        );
    }, [availableOptions, isLoading, hasError]);

    const userFlag: UserFlagSummaryDto | null | undefined = dayDataArray.find(
        (d) => d.data?.userFlag
    )?.data?.userFlag;

    const handleActivityClick = useCallback((activity: ActivityItem) => {
        setSelectedActivity(activity);
        setSidePanelOpen(true);
    }, []);

    const handleSidePanelClose = useCallback(() => {
        setSidePanelOpen(false);
        setSelectedActivity(null);
    }, []);

    const handleFlagSaved = useCallback(
        (userFlagCreated: boolean) => {
            refetch();
            if (userFlagCreated) {
                setTimeout(() => {
                    const flag = dayDataArray.find((d) => d.data?.userFlag)
                        ?.data?.userFlag;
                    if (flag) {
                        setNewUserFlagId(flag.id);
                        setPromptDialogOpen(true);
                    }
                }, 500);
            }
        },
        [refetch, dayDataArray]
    );

    const handleFlagUpdated = useCallback(() => {
        refetch();
    }, [refetch]);

    const handlePromptSaved = useCallback(() => {
        refetch();
    }, [refetch]);

    return (
        <div className="flex flex-col gap-6">
            <UserFlagBanner
                userFlag={userFlag ?? null}
                onFlagUpdated={handleFlagUpdated}
            />

            <div>
                <h3 className="mb-2 text-sm font-semibold">
                    Select Days to Analyze
                </h3>
                <DaySelector
                    selectedDates={selectedDates}
                    onDatesChange={setSelectedDates}
                />
            </div>

            <ActivityFilter
                selectedActivities={selectedActivities}
                onActivityChange={setSelectedActivities}
                availableOptions={availableOptions}
                isLoading={isLoading}
                hasError={hasError}
            />

            {selectedDates.length > 0 && (
                <>
                    <div className="relative">
                        <FullscreenButton
                            onClick={() => setIsFullscreen(true)}
                        />
                        <DaysScrollContainer
                            dayDataArray={dayDataArray}
                            selectedDatesCount={selectedDates.length}
                            onActivityClick={handleActivityClick}
                        />
                    </div>

                    <FullscreenDialog
                        open={isFullscreen}
                        onClose={() => setIsFullscreen(false)}
                        dayDataArray={dayDataArray}
                        selectedDatesCount={selectedDates.length}
                        onActivityClick={handleActivityClick}
                    />
                </>
            )}

            <ActivitySidePanel
                open={sidePanelOpen}
                onClose={handleSidePanelClose}
                activity={selectedActivity}
                userId={user.id.toString()}
                onFlagSaved={handleFlagSaved}
            />

            {newUserFlagId !== null && (
                <UserFlagPromptDialog
                    open={promptDialogOpen}
                    onClose={() => setPromptDialogOpen(false)}
                    userFlagId={newUserFlagId}
                    onSaved={handlePromptSaved}
                />
            )}
        </div>
    );
});

export default FlagsTool;
